import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Permission } from '@brandschutz/shared';
import { useAuth } from '@/lib/auth-context';
import { useCanI } from '@/lib/rbac';
import { Icon } from '@/lib/icons';

interface Props {
  children: ReactNode;
  permission?: Permission;
}

export function ProtectedRoute({ children, permission }: Props) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const hasPermission = useCanI(permission ?? ('project:read' as Permission));

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50">
        <div className="flex flex-col items-center gap-2 text-ink-500">
          <Icon name="Loader2" size={24} className="animate-spin text-accent-500" />
          <p className="text-sm">Sitzung wird geprüft…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (permission && !hasPermission) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="max-w-md rounded-lg border border-ink-200 bg-paper p-8 text-center shadow-elev-1">
          <Icon name="ShieldX" size={32} className="mx-auto text-ink-400" />
          <h2 className="mt-3 text-lg font-semibold text-ink-900">Kein Zugriff</h2>
          <p className="mt-1 text-sm text-ink-600">
            Sie haben nicht die erforderlichen Berechtigungen, um diesen Bereich zu sehen.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
